import axios from 'axios'
import { BOX_TRUCK_OWNER_DOC_TYPES, mapWithConcurrencySettled } from '@numeo/types-core'
import type {
    BoxTruckCompanyDepositValues,
    BoxTruckCompanyDepositSource,
    BoxTruckDriverSsnInput,
    BoxTruckCarrierIdType,
    BoxTruckMcLookupRequest,
    BoxTruckMcLookupResult,
    BoxTruckDocumentAdoptionResult,
    BoxTruckDriverDocumentsCopyResult,
    BoxTruckAssetProfilePatchInput,
    BoxTruckContractorDetailDto,
    BoxTruckAssetDocumentDto,
    BoxTruckAssetDocType,
    BoxTruckContractorUpdateInput,
    BoxTruckManualAssetDetailsInput,
    BoxTruckOwnerDocumentDto,
    BoxTruckOwnerDocType,
    BoxTruckProfileDocumentDto,
    BoxTruckRosterInviteInput,
    BoxTruckDocType,
    BoxTruckDriverProfileDto,
    BoxTruckDriverProfilePatchInput,
    BoxTruckDriverProfileUpdateInput,
    BoxTruckOwnerDriverLinkInput,
    ClientApplication,
} from '@numeo/types-core'
import type { BoxTruckDriverAppLinkDto } from '@numeo/types-main-web-app'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'
import { getServerErrorMessage } from '@/utils/get-user-error-message'
import { withBoxTruckProfileDocumentUploadAnalytics } from '../utils/analytics'
import { keepSourceIdOnSave } from '../utils/documentAutofill/sourceIds'
import { needsDirectBoxTruckDocumentUpload, uploadBoxTruckDocumentDirectly, type BoxTruckProfileDocumentEntity } from './boxTruckDirectDocumentUpload'

const NON_V1_BASE = getApiBaseUrlWithoutV1()
const DRIVER_PROFILE_CACHE_MS = 60_000
const DRIVER_PROFILE_CACHE_MAX_ENTRIES = 100
const driverProfileCache = new Map<string, { data: BoxTruckDriverProfileDto; expiresAt: number }>()
const driverProfileRequests = new Map<string, Promise<BoxTruckDriverProfileDto>>()

const cacheDriverProfile = (driverId: string, data: BoxTruckDriverProfileDto): void => {
    driverProfileCache.delete(driverId)
    // A profile carrying the full SSN is not kept: this cache outlives the form, the 60 s expiry only stops it being
    // served, and nothing clears it on logout, so the plaintext would sit in memory for the rest of the session.
    if (data.registration.ssn || (data.companyDepositSource === 'owner' && data.profile.ownerContractorId)) return
    if (driverProfileCache.size >= DRIVER_PROFILE_CACHE_MAX_ENTRIES) {
        const oldestDriverId = driverProfileCache.keys().next().value
        if (oldestDriverId) driverProfileCache.delete(oldestDriverId)
    }
    driverProfileCache.set(driverId, { data, expiresAt: Date.now() + DRIVER_PROFILE_CACHE_MS })
}

export type BoxTruckPendingDocumentUploads = Partial<Record<BoxTruckDocType, File[]>>
export type BoxTruckPendingOwnerDocumentUploads = Partial<Record<BoxTruckOwnerDocType, File[]>>

/** Files chosen for a vehicle that does not exist yet. The upload endpoint is addressed by asset id, so on
 *  the create form they are held here and sent once the truck has one — the driver form's own arrangement. */
export type BoxTruckPendingAssetDocumentUploads = Partial<Record<BoxTruckAssetDocType, File[]>>

export const fetchBoxTruckContractorProfile = async (contractorId: string): Promise<BoxTruckContractorDetailDto> => {
    const response = await axios.get<{ success: boolean; data: BoxTruckContractorDetailDto }>(`/box-truck-loads/profiles/contractors/${contractorId}/v1`, {
        baseURL: NON_V1_BASE,
    })
    return response.data.data
}

export const fetchBoxTruckOwnerCompanyDeposit = async (contractorId: string, signal?: AbortSignal): Promise<BoxTruckCompanyDepositValues> => {
    const response = await axios.get<{ success: true; data: BoxTruckCompanyDepositValues }>(
        `/box-truck-loads/profiles/contractors/${contractorId}/company-deposit/v1`,
        { baseURL: NON_V1_BASE, signal }
    )
    return response.data.data
}

const fetchBoxTruckDriverProfile = async (driverId: string): Promise<BoxTruckDriverProfileDto> => {
    const cached = driverProfileCache.get(driverId)
    if (cached && cached.expiresAt > Date.now()) return cached.data
    const pending = driverProfileRequests.get(driverId)
    if (pending) return pending

    const request = axios
        .get<{ success: boolean; data: BoxTruckDriverProfileDto }>(`/box-truck-loads/profiles/drivers/${driverId}/v1`, { baseURL: NON_V1_BASE })
        .then((response) => {
            cacheDriverProfile(driverId, response.data.data)
            return response.data.data
        })
        .finally(() => driverProfileRequests.delete(driverId))
    driverProfileRequests.set(driverId, request)
    return request
}

export const fetchBoxTruckDriverProfileOrNull = async (driverId: string): Promise<BoxTruckDriverProfileDto | null> => {
    try {
        return await fetchBoxTruckDriverProfile(driverId)
    } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.status === 404) return null
        throw error
    }
}

/** The profile as the server has it now, skipping the short cache: for after a change made outside this page, like a signed agreement. */
export const refetchBoxTruckDriverProfile = (driverId: string): Promise<BoxTruckDriverProfileDto | null> => {
    driverProfileCache.delete(driverId)
    return fetchBoxTruckDriverProfileOrNull(driverId)
}

/**
 * Adopt a driver created in Profiles into Box Truck, with the details the wizard collected.
 *
 * Only the contractor-linked path used to persist those answers, so a driver an HR added straight from
 * Profiles kept none of them and never became a Box Truck Profiles driver. Caching the result means the
 * Contact Information reads it without another round trip.
 */
export const createBoxTruckDriverProfile = async (driverId: string, details: BoxTruckDriverProfileUpdateInput): Promise<BoxTruckDriverProfileDto> => {
    const response = await axios.post<{ success: boolean; data: BoxTruckDriverProfileDto }>(`/box-truck-loads/profiles/drivers/${driverId}/v1`, details, {
        baseURL: NON_V1_BASE,
    })
    cacheDriverProfile(driverId, response.data.data)
    return response.data.data
}

export const updateBoxTruckDriverProfile = async (driverId: string, input: BoxTruckDriverProfilePatchInput): Promise<BoxTruckDriverProfileDto> => {
    const response = await axios.patch<{ success: boolean; data: BoxTruckDriverProfileDto }>(`/box-truck-loads/profiles/drivers/${driverId}/v1`, input, {
        baseURL: NON_V1_BASE,
    })
    cacheDriverProfile(driverId, response.data.data)
    return response.data.data
}

/** Store a Box Truck driver's full SSN, on its own route: the profile PATCH echoes `expected` values back. */
export const saveBoxTruckDriverSsn = async (driverId: string, ssn: string): Promise<void> => {
    const input: BoxTruckDriverSsnInput = { ssn }
    await axios.put(`/box-truck-loads/profiles/drivers/${driverId}/ssn/v1`, input, { baseURL: NON_V1_BASE })
    // The cached profile carries the SSN it was read with; the next open must read the new one.
    driverProfileCache.delete(driverId)
}

/**
 * Which owner each box-truck driver belongs to.
 *
 * ONE bounded read. It replaces a walk of every 200-row page of the driver summaries — up to forty requests
 * in waves of four, every time the owner picker opened — for two fields per driver. A driver with no owner
 * has no row, which is what the picker reads as free to take, and the server throws rather than truncating
 * because a partial answer offers somebody else's driver as free.
 */
export const fetchBoxTruckDriverOwners = async (): Promise<Map<string, string>> => {
    const response = await axios.get<{ success: boolean; data: { driverId: string; ownerContractorId: string }[] }>(
        '/box-truck-loads/profiles/drivers/owners/v1',
        { baseURL: NON_V1_BASE }
    )
    return new Map((response.data?.data ?? []).map((row) => [row.driverId, row.ownerContractorId]))
}

/**
 * A one-time sign-in link for this driver's app account: opened on their phone, it signs the app in to it, whatever
 * account the app was on. The earlier unused link stops working. Shown once: only a hash of it is stored.
 */
export const createBoxTruckDriverAppLink = async (driverId: string): Promise<BoxTruckDriverAppLinkDto> => {
    const response = await axios.post<{ success: boolean; data: BoxTruckDriverAppLinkDto }>(
        `/box-truck-loads/profiles/drivers/${driverId}/app-link/v1`,
        {},
        { baseURL: NON_V1_BASE }
    )
    // Creating the link may create the driver's account first, so a cached "no account" profile is stale.
    driverProfileCache.delete(driverId)
    return response.data.data
}

export const updateBoxTruckContractorProfile = async (contractorId: string, input: BoxTruckContractorUpdateInput): Promise<void> => {
    await axios.patch(`/box-truck-loads/profiles/contractors/${contractorId}/v1`, input, { baseURL: NON_V1_BASE })
}

/**
 * Resolve a carrier from FMCSA by MC or DOT number.
 *
 * Shared with the driver self-verification flow, which is why it is rate limited per user on the
 * server: it reaches an external resolver on a cache miss. Callers should treat a rejection as
 * "could not confirm" and leave whatever the operator typed alone.
 */
export const lookupBoxTruckCarrier = async (number: string, type: BoxTruckCarrierIdType = 'mc'): Promise<BoxTruckMcLookupResult> => {
    // The body is typed, not spelled out: the server reads `number`, and a key it does not read comes
    // back as a plain 404 — "no carrier found" for a carrier that exists, on every single lookup.
    const body: BoxTruckMcLookupRequest = { type, number }
    const response = await axios.post<{ success: boolean; data: BoxTruckMcLookupResult }>('/box-truck-loads/verifications/mc/lookup/v1', body, {
        baseURL: NON_V1_BASE,
    })
    return response.data.data
}

export const linkBoxTruckContractorDriver = async (
    contractorId: string,
    driverId: string,
    details?: BoxTruckDriverProfileUpdateInput,
    companyDepositSource?: BoxTruckCompanyDepositSource
): Promise<void> => {
    const input: BoxTruckOwnerDriverLinkInput = { ...details, ...(companyDepositSource ? { companyDepositSource } : {}) }
    await axios.post(`/box-truck-loads/profiles/contractors/${contractorId}/drivers/${driverId}/v1`, input, { baseURL: NON_V1_BASE })
    // The cached profile names the owner it was read with: reopened within the minute, the driver would show no
    // owner and offer to create one again.
    driverProfileCache.delete(driverId)
}

/** Keep the owner assignment and take a current, independently editable copy of their company and deposit. */
export const switchBoxTruckDriverToOwnDetails = async (driverId: string): Promise<BoxTruckCompanyDepositValues> => {
    const response = await axios.post<{ success: boolean; data: BoxTruckCompanyDepositValues }>(
        `/box-truck-loads/profiles/drivers/${driverId}/company-deposit/own/v1`,
        {},
        { baseURL: NON_V1_BASE }
    )
    driverProfileCache.delete(driverId)
    return response.data.data
}

export const removeBoxTruckDriverOwner = async (driverId: string): Promise<void> => {
    await axios.delete(`/box-truck-loads/profiles/drivers/${driverId}/owner/v1`, {
        baseURL: NON_V1_BASE,
    })
    driverProfileCache.delete(driverId)
}

/**
 * Put a driver's W-9 and voided check on the owner created from them. Copied inside storage by the server, never
 * through the browser. Repeatable: a retry finds what the first attempt copied.
 */
export const copyBoxTruckDriverDocumentsToOwner = async (contractorId: string, driverId: string): Promise<BoxTruckDriverDocumentsCopyResult> => {
    const response = await axios.post<{ success: boolean; data: BoxTruckDriverDocumentsCopyResult }>(
        `/box-truck-loads/profiles/contractors/${contractorId}/documents/copy-from-driver/v1`,
        { driverId },
        { baseURL: NON_V1_BASE }
    )
    return response.data.data
}

/**
 * Give a vehicle an owner.
 *
 * `onlyIfUnowned` is decided by the SERVER, inside the write: it is for the caller adopting a truck on behalf
 * of a driver being created, and checking a cached truck list here would decide against a snapshot that may
 * be minutes old. A vehicle someone else has claimed in the meantime answers 409 rather than changing hands.
 */
export const linkBoxTruckContractorAsset = async (
    contractorId: string,
    assetId: string,
    details?: BoxTruckManualAssetDetailsInput,
    options?: { onlyIfUnowned?: boolean }
): Promise<void> => {
    await axios.post(`/box-truck-loads/profiles/contractors/${contractorId}/assets/${assetId}/v1`, details, {
        baseURL: NON_V1_BASE,
        ...(options?.onlyIfUnowned ? { params: { onlyIfUnowned: 'true' } } : {}),
    })
}

/** Clear a vehicle's owner. Separate call from linking: there is no contractor to name when removing one. */
export const unlinkBoxTruckAssetOwner = async (assetId: string): Promise<void> => {
    await axios.delete(`/box-truck-loads/profiles/assets/${assetId}/owner/v1`, { baseURL: NON_V1_BASE })
}

export const updateBoxTruckAssetProfile = async (assetId: string, input: BoxTruckAssetProfilePatchInput): Promise<void> => {
    await axios.patch(`/box-truck-loads/profiles/assets/${assetId}/v1`, input, { baseURL: NON_V1_BASE })
}

/**
 * Put the driver's VEHICLE paperwork — registration, insurance, cargo photo — on the vehicle they work.
 *
 * Copies metadata, never the file: both records name the same stored object. Idempotent, so a retry after a
 * dropped connection costs nothing. Returns how many were newly adopted.
 */
export const adoptDriverVehicleDocumentsToAsset = async (assetId: string, driverId: string): Promise<BoxTruckDocumentAdoptionResult> => {
    const response = await axios.post<{ success: boolean; data: BoxTruckDocumentAdoptionResult }>(
        `/box-truck-loads/profiles/assets/${assetId}/documents/adopt-from-driver/v1`,
        { driverId },
        { baseURL: NON_V1_BASE }
    )
    return response.data.data
}

export const updateBoxTruckAssetDrivers = async (assetId: string, driverIds: string[]): Promise<ClientApplication> => {
    const response = await axios.put<{ application: ClientApplication }>(
        `/box-truck-loads/profiles/assets/${assetId}/drivers/v1`,
        { drivers: driverIds },
        {
            baseURL: NON_V1_BASE,
        }
    )
    return response.data.application
}

type ProfileEntity = BoxTruckProfileDocumentEntity

/** A saved profile document as an object URL, to preview it. */
export const fetchBoxTruckProfileDocumentBlobUrl = async (entity: ProfileEntity, entityId: string, documentId: string): Promise<string> => {
    const response = await axios.get<Blob>(`/box-truck-loads/profiles/${entity}/${entityId}/documents/${documentId}/file/v1`, {
        baseURL: NON_V1_BASE,
        responseType: 'blob',
    })
    return URL.createObjectURL(response.data)
}

const uploadDocument = async <D, T extends string>(entity: ProfileEntity, entityId: string, type: T, file: File): Promise<D> => {
    // Too large to pass through the API at all — see boxTruckDirectDocumentUpload.ts.
    if (needsDirectBoxTruckDocumentUpload(file)) return uploadBoxTruckDocumentDirectly<D, T>(entity, entityId, type, file)
    const form = new FormData()
    form.append('file', file)
    const response = await axios.post<{ success: boolean; data: D }>(
        `/box-truck-loads/profiles/${entity}/${entityId}/documents/v1?type=${encodeURIComponent(type)}`,
        form,
        { baseURL: NON_V1_BASE }
    )
    return response.data.data
}

export const fetchBoxTruckContractorDocumentBlobUrl = (contractorId: string, documentId: string): Promise<string> =>
    fetchBoxTruckProfileDocumentBlobUrl('contractors', contractorId, documentId)

export const uploadBoxTruckContractorDocument = (contractorId: string, type: BoxTruckOwnerDocType, file: File): Promise<BoxTruckOwnerDocumentDto> =>
    uploadDocument('contractors', contractorId, type, file)

export const removeBoxTruckContractorDocument = (contractorId: string, documentId: string): Promise<void> =>
    axios.delete(`/box-truck-loads/profiles/contractors/${contractorId}/documents/${documentId}/v1`, { baseURL: NON_V1_BASE }).then(() => undefined)

/** A file that waited for the profile's save is saved: the document keeps the id AI autofill knew the file by (`keepSourceIdOnSave`). */
const keepingSourceId =
    (file: File) =>
    <D extends { documentId: string }>(document: D): D => {
        keepSourceIdOnSave(file, document.documentId)
        return document
    }

export const uploadPendingOwnerDocuments = async (contractorId: string, pending: BoxTruckPendingOwnerDocumentUploads): Promise<number> => {
    const uploads = BOX_TRUCK_OWNER_DOC_TYPES.flatMap((type) => (pending[type] ?? []).map((file) => ({ type, file })))
    const results = await mapWithConcurrencySettled(uploads, MAX_PARALLEL_DOCUMENT_UPLOADS, ({ type, file }) =>
        uploadBoxTruckContractorDocument(contractorId, type, file).then(keepingSourceId(file))
    )
    return results.filter((result) => result.status === 'rejected').length
}

export const uploadBoxTruckProfileDriverDocument = async (driverId: string, type: BoxTruckDocType, file: File): Promise<BoxTruckProfileDocumentDto> => {
    const document = await uploadDocument<BoxTruckProfileDocumentDto, BoxTruckDocType>('drivers', driverId, type, file)
    driverProfileCache.delete(driverId)
    return document
}

/** Run pending driver uploads through one bounded queue; callers choose the immediate or analytics-wrapped upload boundary. */
export const uploadPendingDriverDocuments = async (
    driverId: string,
    pending: BoxTruckPendingDocumentUploads,
    upload: (driverId: string, type: BoxTruckDocType, file: File) => Promise<BoxTruckProfileDocumentDto> = uploadBoxTruckProfileDriverDocument
): Promise<number> => {
    const uploads = Object.entries(pending).flatMap(([type, files]) => (files ?? []).map((file) => ({ type: type as BoxTruckDocType, file })))
    const results = await mapWithConcurrencySettled(uploads, MAX_PARALLEL_DOCUMENT_UPLOADS, ({ type, file }) =>
        upload(driverId, type, file).then(keepingSourceId(file))
    )
    return results.filter((result) => result.status === 'rejected').length
}

export const fetchBoxTruckProfileDriverDocumentBlobUrl = (driverId: string, documentId: string): Promise<string> =>
    fetchBoxTruckProfileDocumentBlobUrl('drivers', driverId, documentId)

export const removeBoxTruckProfileDriverDocument = async (driverId: string, documentId: string): Promise<void> => {
    await axios.delete(`/box-truck-loads/profiles/drivers/${driverId}/documents/${documentId}/v1`, { baseURL: NON_V1_BASE })
    driverProfileCache.delete(driverId)
}

export const fetchBoxTruckAssetDocuments = async (assetId: string): Promise<BoxTruckAssetDocumentDto[]> => {
    const response = await axios.get<{ success: boolean; data: BoxTruckAssetDocumentDto[] }>(`/box-truck-loads/profiles/assets/${assetId}/documents/v1`, {
        baseURL: NON_V1_BASE,
    })
    return response.data.data
}

export const uploadBoxTruckAssetDocument = (assetId: string, type: BoxTruckAssetDocType, file: File): Promise<BoxTruckAssetDocumentDto> =>
    withBoxTruckProfileDocumentUploadAnalytics('vehicle', type, file, () =>
        uploadDocument<BoxTruckAssetDocumentDto, BoxTruckAssetDocType>('assets', assetId, type, file)
    )

export const removeBoxTruckAssetDocument = async (assetId: string, documentId: string): Promise<void> => {
    await axios.delete(`/box-truck-loads/profiles/assets/${assetId}/documents/${documentId}/v1`, { baseURL: NON_V1_BASE })
}

/**
 * How many uploads may be in flight at once.
 *
 * Matches the server's per-instance memory-upload ceiling. Past it the endpoint answers 429 ("Too many
 * uploads in progress"), so an operator attaching eight photos would see most of them fail rather than
 * queue. Shared with the document grid so both sides of the same limit cannot drift apart.
 */
export const MAX_PARALLEL_DOCUMENT_UPLOADS = 4

interface PendingAssetDocumentUploadResult {
    failedCount: number
    /** Successful files are removed so a later Save can retry only the failures. */
    remaining: BoxTruckPendingAssetDocumentUploads
    /** One bounded reason for the operator; never includes file contents or all failure messages. */
    firstFailureMessage?: string
}

/**
 * Send the files a create form has been holding, now that the vehicle exists.
 *
 * Returns how many failed rather than throwing: the truck is already created and its details saved, so a
 * failed photo is a thing to REPORT, not a reason to present the whole save as having gone wrong. The
 * operator can add the rest from the vehicle's own page.
 */
export const uploadPendingAssetDocuments = async (assetId: string, pending: BoxTruckPendingAssetDocumentUploads): Promise<PendingAssetDocumentUploadResult> => {
    const uploads = Object.entries(pending).flatMap(([type, files]) => (files ?? []).map((file) => ({ type: type as BoxTruckAssetDocType, file })))
    if (uploads.length === 0) return { failedCount: 0, remaining: {} }
    const results = await mapWithConcurrencySettled(uploads, MAX_PARALLEL_DOCUMENT_UPLOADS, ({ type, file }) =>
        uploadBoxTruckAssetDocument(assetId, type, file).then(keepingSourceId(file))
    )
    const remaining: BoxTruckPendingAssetDocumentUploads = {}
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') return
        const { type, file } = uploads[index]
        remaining[type] = [...(remaining[type] ?? []), file]
    })
    const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected')
    const firstFailureMessage = getServerErrorMessage(firstFailure?.reason) ?? (firstFailure?.reason instanceof Error ? firstFailure.reason.message : undefined)
    return {
        failedCount: results.filter((result) => result.status === 'rejected').length,
        remaining,
        ...(firstFailureMessage ? { firstFailureMessage } : {}),
    }
}

/**
 * Invite an existing Numeo account onto this roster, after the create answered `INVITE_REQUIRED`.
 *
 * Nothing is claimed — the person joins when they accept on their phone, and their own account and login
 * are untouched, which is what makes this safe for a driver another company already works with.
 *
 * It answers with the row the invite left behind, so everything the form collected is attached to it right
 * away, exactly as it is after a create: the operator sees the driver listed as invited instead of a page
 * that looks like it saved nothing.
 */
export const inviteBoxTruckRosterDriver = async (input: BoxTruckRosterInviteInput): Promise<{ driverId?: string }> => {
    const response = await axios.post<{ success: boolean; data: { driverId?: string } }>('/box-truck-loads/profiles/drivers/invite/v1', input, {
        baseURL: NON_V1_BASE,
    })
    return response.data.data ?? {}
}
